import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { MENU_SECTIONS } from "@/data/content";

export default function Menus() {
  const [active, setActive] = useState(MENU_SECTIONS[0].id);
  const refs = useRef({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-30% 0px -55% 0px", threshold: 0 }
    );
    MENU_SECTIONS.forEach((s) => {
      const el = refs.current[s.id];
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const scrollTo = (id) => {
    const el = refs.current[id];
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="menus-page">
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-16 lg:pt-24 pb-12">
        <div className="eyebrow mb-6">Out of Eden Menus</div>
        <h1 className="font-serif-display text-5xl lg:text-7xl max-w-4xl leading-[1.04]" style={{ color: "var(--ooe-ink)" }}>
          A living kitchen — written each season, for each table.
        </h1>
        <p className="mt-8 max-w-2xl text-[16px] leading-relaxed" style={{ color: "var(--ooe-ink-2)" }}>
          The following is a curated sample of our seasonal repertoire. Every
          menu is custom-built around your event, dietary preferences and the
          ingredients at their peak.
        </p>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-32">
        <div className="grid lg:grid-cols-12 gap-12">
          {/* Sticky sidebar */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-[96px]">
              <div className="eyebrow mb-5">Sections</div>
              <nav className="flex lg:flex-col gap-2 overflow-x-auto scroll-hide pb-4 lg:pb-0">
                {MENU_SECTIONS.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className="text-left whitespace-nowrap py-2 text-[14px] tracking-wide transition-colors"
                    style={{
                      color: active === s.id ? "var(--ooe-brand)" : "var(--ooe-ink-2)",
                      borderLeft: active === s.id ? "2px solid var(--ooe-brand)" : "2px solid transparent",
                      paddingLeft: 10,
                    }}
                    data-testid={`menu-nav-${s.id}`}
                  >
                    {s.title}
                  </button>
                ))}
              </nav>
            </div>
          </aside>

          {/* Content */}
          <div className="lg:col-span-9 space-y-24">
            {MENU_SECTIONS.map((s) => (
              <div
                key={s.id}
                id={s.id}
                ref={(el) => (refs.current[s.id] = el)}
                style={{ scrollMarginTop: 96 }}
                data-testid={`menu-section-${s.id}`}
              >
                <div className="mb-10 pb-6" style={{ borderBottom: "1px solid var(--ooe-border)" }}>
                  <div className="eyebrow mb-3">Section</div>
                  <h2 className="font-serif-display text-4xl lg:text-6xl" style={{ color: "var(--ooe-ink)" }}>{s.title}</h2>
                  <p className="mt-4 text-[15px] max-w-xl" style={{ color: "var(--ooe-ink-2)" }}>{s.blurb}</p>
                </div>
                <div className="grid sm:grid-cols-2 gap-x-10 gap-y-10">
                  {s.items.map((it) => (
                    <article key={it.name}>
                      <h3 className="font-serif-display text-2xl mb-2" style={{ color: "var(--ooe-ink)" }}>{it.name}</h3>
                      <p className="text-[14px] leading-relaxed" style={{ color: "var(--ooe-ink-2)" }}>{it.desc}</p>
                    </article>
                  ))}
                </div>
              </div>
            ))}

            <div className="pt-10" style={{ borderTop: "1px solid var(--ooe-border)" }}>
              <p className="text-[15px] max-w-xl mb-6" style={{ color: "var(--ooe-ink-2)" }}>
                Looking for something not listed? Every menu is bespoke — share
                what you have in mind and we'll write something just for you.
              </p>
              <Link to="/contact" className="btn-primary inline-flex" data-testid="menus-contact-cta">
                Build a Custom Menu <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
